'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { isIOSSafari } from '@/app/_lib/detect-ios-safari';

type DownloadStatus = 'idle' | 'loading' | 'success' | 'error';

interface UseDownloadOptions {
  /** Callback when download succeeds */
  onSuccess?: () => void;
  /** Callback when download fails */
  onError?: (error: Error) => void;
  /** Duration to show success state in ms (default: 1500) */
  successDuration?: number;
  /** Duration to show error state in ms (default: 2000) */
  errorDuration?: number;
}

interface UseDownloadReturn {
  status: DownloadStatus;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isIOSSafari: boolean;
  download: (url: string, filename: string) => Promise<void>;
  cancel: () => void;
}

/**
 * Hook for handling file downloads with loading states and iOS Safari support.
 *
 * Features:
 * - Desktop/Android: Downloads file via fetch + blob, shows loading state
 * - iOS Safari: Opens file in new tab (Safari ignores Content-Disposition: attachment)
 * - AbortController support for cancelling downloads
 * - Proper cleanup on unmount (no memory leaks)
 */
export function useDownload(options: UseDownloadOptions = {}): UseDownloadReturn {
  const {
    onSuccess,
    onError,
    successDuration = 1500,
    errorDuration = 2000,
  } = options;

  const [status, setStatus] = useState<DownloadStatus>('idle');

  // Refs for cleanup
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Abort any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const safeSetStatus = useCallback((newStatus: DownloadStatus) => {
    if (isMountedRef.current) {
      setStatus(newStatus);
    }
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    clearPendingTimeout();
    safeSetStatus('idle');
  }, [clearPendingTimeout, safeSetStatus]);

  const download = useCallback(
    async (url: string, filename: string) => {
      // Prevent concurrent downloads (race condition fix)
      if (status === 'loading') {
        return;
      }

      // iOS Safari: Open in new tab since it ignores attachment header
      // Don't call onSuccess - we don't know if download actually succeeded
      if (isIOSSafari()) {
        window.open(url, '_blank');
        return;
      }

      // Clear any previous timeout
      clearPendingTimeout();

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      safeSetStatus('loading');

      let objectUrl: string | null = null;

      try {
        const response = await fetch(url, {
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          throw new Error(
            `Download failed (${response.status}): ${response.statusText || errorText || 'Unknown error'}`
          );
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        // Create temporary link and trigger download
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        safeSetStatus('success');
        onSuccess?.();

        // Reset to idle after success duration
        timeoutRef.current = setTimeout(() => {
          safeSetStatus('idle');
        }, successDuration);
      } catch (err) {
        // Don't show error for aborted requests
        if (err instanceof Error && err.name === 'AbortError') {
          safeSetStatus('idle');
          return;
        }

        const error = err instanceof Error ? err : new Error('Download failed');
        console.error('Download error:', error);

        safeSetStatus('error');
        onError?.(error);

        // Reset to idle after error duration
        timeoutRef.current = setTimeout(() => {
          safeSetStatus('idle');
        }, errorDuration);
      } finally {
        // Always clean up blob URL
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        abortControllerRef.current = null;
      }
    },
    [
      status,
      onSuccess,
      onError,
      successDuration,
      errorDuration,
      clearPendingTimeout,
      safeSetStatus,
    ]
  );

  return {
    status,
    isLoading: status === 'loading',
    isSuccess: status === 'success',
    isError: status === 'error',
    isIOSSafari: isIOSSafari(),
    download,
    cancel,
  };
}
